import {
	FC,
	HTMLAttributes,
	ReactNode,
	useEffect,
	useMemo,
	useState,
} from 'react';
import cn from 'classnames';
import { Popover, Button, TextHint } from 'src/Components';
import SelectBoxBody from './SelectBoxBody';
import { useDebounce, useToggle } from 'src/Hooks';
import { ReactComponent as ArrowIcon } from 'src/Themes/assets/arrow.svg';
import { ReactComponent as ClearIcon } from 'src/Themes/assets/clear.svg';
import st from './SelectBox.module.scss';

interface ISelectBoxProps extends HTMLAttributes<HTMLDivElement> {
	/** Поисковая строка по умолчанию */
	defaultSearchTerm?: string;

	minSearchLength?: number;
	/** Плэйсхолдер */
	placeholder?: string;
	/** Лимит вариантов */
	searchLimit?: number;
	/** Разрешить очистку результата */
	allowClear?: boolean;
	/** Только для чтения */
	readonly?: boolean;
	/** Флаг ошибки в поле */
	isInvalidate?: boolean;

	/** Автофокус на поле ввода */
	autoFocus?: boolean;

	isWait?: boolean;
	allowAdd?: boolean;
	multiple?: boolean;
	defaultOpen?: boolean;
	selectedValues: Array<any>;
	onChangeSelected?: (selected: Array<any>) => void;

	options: Array<any>;
	valueField?: string;
	titleField?: string;

	onSearch?: (searchTerm: string) => void;
	onChangeOpen?: (isOpen: boolean) => void;
	onCreated?: (itemText: string) => void;
	onRenderOption?: (
		item: any,
		isSelected: boolean,
		searchText: string,
		index: number,
		getHtml: (source: string, searchText: string) => string,
	) => ReactNode;

	renderListTb?: () => ReactNode;
	onCancel?: () => void;
	onApply?: (selected: any[]) => void;
}

/** Базовая вьюшка выпадающего списка */
const SelectBox: FC<ISelectBoxProps> = (props) => {
	const {
		isWait,
		allowClear,
		placeholder,
		readonly,
		isInvalidate,
		defaultOpen = false,
		autoFocus = false,
		options,
		valueField = 'id',
		titleField = 'title',
		multiple = false,
		allowAdd = false,
		defaultSearchTerm = '',
		minSearchLength = 0,
		onSearch,
		selectedValues = [],
		onChangeSelected,
		onChangeOpen,
		onCreated,
		renderListTb,
		onCancel,
		onApply,
		onRenderOption,
		onBlur,
		className,
		onScroll,
		...otherProps
	} = props;

	const [isOpened, setIsOpen] = useState<boolean>(defaultOpen);
	const [search, setSearch] = useDebounce<string>(
		onChangeSearchHandler,
		defaultSearchTerm,
	);
	const [selected, setSelected] = useState<any[]>([]);
	const [isNeedRecalcPos, recalc] = useToggle(false);

	useEffect(() => {
		if (defaultOpen && !isOpened) {
			setIsOpen(true);
		}
	}, [defaultOpen]);

	/** При открытии и изменениях в опциях, обновляем позицию выпадающего списка */
	useEffect(() => {
		if (!isOpened) {
			setSearch('');
		}
		recalc();
		// eslint-disable-next-line
	}, [isOpened, options]);

	useEffect(() => {
		if (!selectedValues.length && !selected.length) {
			return;
		}
		const list: any[] = [];
		if (!!selectedValues && !!selectedValues.length) {
			selectedValues.forEach((si) => {
				if (si) {
					if (typeof si === 'object') {
						list.push({ ...si });
					} else {
						const opt = options.find((o) => {
							return o[valueField] === si;
						});
						if (opt) {
							list.push(opt);
						}
					}
				}
			});
		}
		setSelected(list);
	}, [isOpened, options, selectedValues]);

	/** Для внешнего поиска ( вызывается с задержкой ) */
	function onChangeSearchHandler(search: string) {
		if (onSearch) {
			if (
				!search.length ||
				minSearchLength <= 0 ||
				search.length >= minSearchLength
			) {
				onSearch(search);
			} else {
				onSearch('');
			}
		}
	}

	/** Функция открытия/скрытия списка */
	const changeOpen = (open?: boolean) => {
		if (readonly) {
			return;
		}
		setIsOpen((pr) => {
			const newState = open !== undefined ? open : !pr;
			if (onChangeOpen) {
				onChangeOpen(newState);
			}
			return newState;
		});
	};

	/** Очистка поиска/выбранных опций */
	const onClear = () => {
		if (isOpened) {
			setSearch('');
		} else {
			setSelected([]);
			if (onChangeSelected) {
				onChangeSelected([]);
			}
		}
	};

	/** Отмена ( стандартный тулбар ) */
	const onCancelHandler = () => {
		setIsOpen(false);
		if (onCancel) {
			onCancel();
		}
	};

	/** Применение значения ( стандартный тулбар ) */
	const onApplyHandler = () => {
		setIsOpen(false);
		if (onApply) {
			onApply(selected);
		}
	};

	/** Проверка на наличие создаваемого элемента в списке */
	const checkExistNewItem = (itemTitle: string) => {
		if (!itemTitle) {
			return false;
		}
		const checkExistItem = (opt: any) => {
			if (typeof opt === 'object') {
				return [opt[valueField], opt[titleField]].includes(itemTitle);
			}
			return opt === itemTitle;
		};
		return !!searchedOptions.filter(checkExistItem).length;
	};

	/** Событие клика по опции */
	const onClickOptionHandler = (item: any) => {
		if (allowAdd && item.isNew && onCreated) {
			onCreated(item[valueField]);
		}
		changeSelectedItem(item);
	};

	/** Функция выбора опции */
	const changeSelectedItem = (item: any) => {
		if (multiple) {
			setSelected((pr) => {
				const newList = [...pr];
				const index = newList.findIndex((o) => {
					return o === item;
				});
				if (index !== -1) {
					newList.splice(index, 1);
				} else {
					newList.push(item);
				}

				if (onChangeSelected) {
					onChangeSelected(newList);
				}
				return newList;
			});
		} else {
			const newList = [item];
			setSelected(newList);
			if (onChangeSelected) {
				onChangeSelected(newList);
			}
			changeOpen(false);
			setSearch(item[titleField]);
		}
	};

	/** Обработчик клика вне списка */
	const onOutsideClickHandler = () => {
		if (isOpened) {
			changeOpen(false);
		}
	};

	/** Склеиваем выбранные тайтлы опций в строку */
	const getSelectedTitles = () => {
		return selected
			.map((s) => {
				return typeof s === 'object' ? s[titleField] : s.toString();
			})
			.join(', ');
	};

	/** Замемоизированный список опций попадающий под фильтр, если не указана внешня функция фильтрации */
	const searchedOptions = useMemo(() => {
		if (onSearch) {
			return options;
		}
		return options.filter((opt) => {
			const term = (search ?? '').toLocaleLowerCase();
			if (typeof opt === 'object') {
				return (
					(opt[titleField] ?? '')
						.toLocaleLowerCase()
						.indexOf(term) !== -1
				);
			} else {
				return opt.toString().toLocaleLowerCase().indexOf(term) !== -1;
			}
		});
	}, [options, search]);

	/** Рендер кнопок отмена/применить */
	const renderButtons = () => {
		if (!onApply || !selected.length) {
			return null;
		}
		return (
			<div className={st.modalButtonsSB}>
				<Button
					id={'std-tb_cancel'}
					onClick={onCancelHandler}
					variant="red"
					outline
				>
					Отмена
				</Button>
				<Button id={'std-tb_apply'} onClick={onApplyHandler}>
					Применить
				</Button>
			</div>
		);
	};

	/** Отрисовка тела выпадающего списка */
	const renderList = () => {
		let optionList;
		if (allowAdd && !!search.length && !checkExistNewItem(search)) {
			const opt = {
				[valueField]: search ?? 'new',
				newTitle: `Создать "${search}"`,
				isNew: true,
			};
			optionList = [opt, ...searchedOptions];
		} else {
			optionList = searchedOptions;
		}

		return (
			<SelectBoxBody
				minSearchLength={minSearchLength}
				isWait={isWait}
				options={optionList}
				multiple={multiple}
				valueField={valueField}
				titleField={titleField}
				searchText={search}
				selectedIds={selected.map((s) => {
					return s[valueField];
				})}
				onClickOption={onClickOptionHandler}
				renderListTb={renderListTb ? renderListTb : renderButtons}
				onRenderOption={onRenderOption}
				onScroll={onScroll}
			/>
		);
	};

	/** Отрисовка тулбара */
	const renderTB = () => {
		return (
			<div className={st.toolbar}>
				{!!isWait && <div className={st.spinner} />}
				{allowClear && !!isOpened && !!search.length && (
					<ClearIcon width={24} height={24} onClick={onClear} />
				)}
				{allowClear && !isOpened && !!selected.length && (
					<ClearIcon width={24} height={24} onClick={onClear} />
				)}
			</div>
		);
	};

	/** Отрисовка инпута ввода */
	const renderInput = () => {
		return (
			<input
				value={search}
				placeholder={placeholder ?? 'Поиск по списку'}
				readOnly={readonly}
				onChange={(e) => {
					return setSearch(e.target.value);
				}}
				autoFocus={autoFocus}
			/>
		);
	};

	/** Отрисовка выбранных значений */
	const renderTitles = () => {
		const isPlaceHolder = !selected.length;
		return (
			<TextHint
				className={cn('body-font', { [st.placeholder]: isPlaceHolder })}
				isList={multiple}
				onClick={() => {
					return changeOpen(true);
				}}
			>
				{!isPlaceHolder ? getSelectedTitles() : (placeholder ?? '')}
			</TextHint>
		);
	};

	const classes = cn(st.wrapper, className, {
		[st.opened]: isOpened,
		[st.invalidate]: isInvalidate && !isOpened,
		[st.readonly]: readonly,
		[st.placeholder]: !selectedValues.length,
	});

	return (
		<Popover
			isOpened={isOpened}
			isNeedRecalcPos={isNeedRecalcPos}
			setIsOpen={changeOpen}
			onOutsideClick={onOutsideClickHandler}
			renderBody={renderList}
			className={classes}
			bodyClass={st.bodyList}
			isRevertClass={st.upper}
			{...otherProps}
		>
			<ArrowIcon
				onClick={() => {
					return changeOpen();
				}}
			/>
			{isOpened ? renderInput() : renderTitles()}
			{!readonly && renderTB()}
		</Popover>
	);
};

export default SelectBox;
